/** SearchPanel state hook — owns query, debounced fetch, results, filter. */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  SEARCH_FILTER_CHIPS,
  type FilterChip,
  type SearchResult,
} from "./searchHelpers";

export interface UseSearchStateResult {
  query: string;
  setQuery: (q: string) => void;
  results: SearchResult[];
  facets: Record<string, number>;
  totalResults: number;
  loading: boolean;
  activeChip: FilterChip;
  setActiveChip: (chip: FilterChip) => void;
}

export function useSearchState(): UseSearchStateResult {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [facets, setFacets] = useState<Record<string, number>>({});
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeChip, setActiveChip] = useState<FilterChip>(SEARCH_FILTER_CHIPS[0]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // Debounced search whenever query OR active chip changes.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setFacets({});
      setTotalResults(0);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      let orgId = (window as unknown as { __currentOrgId?: string }).__currentOrgId || "";
      if (!orgId) {
        try {
          const { useAuthStoreV1 } = await import("@/stores/authStore_v1");
          orgId = useAuthStoreV1.getState().selectedOrg?.id || "";
        } catch { /* */ }
      }
      if (!orgId) return;

      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const { searchMetadata } = await import("../../devAgentService");
        const data = await searchMetadata({ query: trimmed, org_id: orgId, types: activeChip.types, limit: 50 });
        if (requestId !== requestIdRef.current) return; // stale
        setResults(data?.results || []);
        setFacets(data?.facets || {});
        setTotalResults(data?.total ?? data?.results?.length ?? 0);
      } catch {
        if (requestId === requestIdRef.current) {
          setResults([]);
          setFacets({});
          setTotalResults(0);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, activeChip]);

  return {
    query, setQuery, results, facets, totalResults, loading,
    activeChip, setActiveChip,
  };
}
